import { RadioButtonGroup, type RadioButtonGroupProps, type RadioButtonProps } from '@vizit/molecules';
import * as React from 'react';

const RADIO_BUTTON_TEXT_MAP: Record<string, { title: string; subtitle: string; imageAlt: string }> = {
  original_image: {
    title: 'Original image',
    subtitle: 'The original image without any overlays applied.',
    imageAlt: 'Original image',
  },
  attention_map: {
    title: 'Attention map',
    subtitle: "Attention maps reveal what viewer's will notice most and least in your content.",
    imageAlt: 'Attention map',
  },
  focal_points: {
    title: 'Focal points',
    subtitle: 'Focal points determine which content elements have the highest probability of standing out.',
    imageAlt: 'Focal points',
  },
  gaze_sequence: {
    title: 'Gaze sequence',
    subtitle: "Gaze sequences reveal where a viewer's gaze is directed when looking at your content.",
    imageAlt: 'Gaze sequence',
  },
  visual_appeal_drivers: {
    title: 'Visual appeal drivers',
    subtitle: 'Visual appeal drivers show which elements are most appealing to your audience.',
    imageAlt: 'Visual appeal drivers',
  },
  visual_appeal_detractors: {
    title: 'Visual appeal detractors',
    subtitle: 'Visual appeal detractors show which elements are least appealing to your audience.',
    imageAlt: 'Visual appeal detractors',
  },
};

interface ArtifactsRadioButtonGroupProps extends Omit<RadioButtonGroupProps, 'options' | 'isDisabled' | 'aria-label'> {
  /**
   * The artifact images to display in the radio button group.
   */
  artifactImages: Record<
    | 'original_image'
    | 'attention_map'
    | 'focal_points'
    | 'gaze_sequence'
    | 'visual_appeal_drivers'
    | 'visual_appeal_detractors',
    { data: { url: string; thumbnailUrl?: string }; isLoading: boolean; error?: unknown }
  >;
  /**
   * Whether the radio button group is loading.
   */
  isLoading?: boolean;
  /**
   * Custom labels to override default text for specific artifacts.
   * Only the fields you want to override need to be provided.
   */
  customLabels?: Partial<Record<string, { title?: string; subtitle?: string; imageAlt?: string }>>;
}

/**
 * A radio button group that displays the artifact images and their corresponding titles, subtitles, and image alt text.
 *
 * @param props - The props for the artifacts radio button group.
 * @returns A radio button group that displays the artifact images and their corresponding titles, subtitles, and image alt text.
 *
 * @example
 * ```tsx
 * <ArtifactsRadioButtonGroup
 *   artifactImages={artifactImages}
 *   isLoading={isLoading}
 *   onChange={onChange}
 *   value={value}
 * />
 * ```
 */
const ArtifactsRadioButtonGroup = (props: ArtifactsRadioButtonGroupProps) => {
  const { artifactImages, isLoading, customLabels, ...rest } = props;

  const options: RadioButtonProps[] = React.useMemo(
    () =>
      Object.entries(artifactImages).map(([key, { data, isLoading: isAnalysisItemLoading }]) => {
        const defaultLabels = RADIO_BUTTON_TEXT_MAP[key];
        const customLabel = customLabels?.[key];

        return {
          ...defaultLabels,
          ...(customLabel && {
            title: customLabel.title ?? defaultLabels.title,
            subtitle: customLabel.subtitle ?? defaultLabels.subtitle,
            imageAlt: customLabel.imageAlt ?? defaultLabels.imageAlt,
          }),
          value: key,
          hasImage: true,
          imageSrc: data?.thumbnailUrl ?? data?.url ?? null,
          fallbackImageSrc: data?.url ?? undefined,
          isImageLoading: isLoading || isAnalysisItemLoading,
          isDisabled: isLoading || isAnalysisItemLoading || !data,
        };
      }),
    [artifactImages, isLoading, customLabels],
  );

  return (
    <RadioButtonGroup
      aria-label="Image analysis options"
      options={options}
      className="flex flex-col gap-8 max-lg:grid max-lg:grid-cols-2 max-lg:gap-8 max-md:grid-cols-1 max-md:justify-items-stretch"
      isDisabled={isLoading}
      isLoading={isLoading}
      {...rest}
    />
  );
};

export { ArtifactsRadioButtonGroup };
export type { ArtifactsRadioButtonGroupProps };
